import {
  AlertTriangleIcon,
  DropletIcon,
  ConeIcon,
  TrashIcon,
  TentTreeIcon,
  BookOpenIcon,
  FileCheckIcon,
  ReceiptIcon,
  PartyPopperIcon,
  InfoIcon,
  type LucideIcon,
} from 'lucide-react'
import type { Localized } from '../types/notice'
import type { City } from '../utils/activeCity'
import { BROSSARD_ADDRESSES, BROSSARD_CENTER, BROSSARD_SECTORS } from './brossard'

export type Coord = [number, number]

/** Geographic centre of Windsor, QC — shared by every map in the module. */
export const WINDSOR_CENTER: Coord = [45.5672, -71.994]

// --- CATEGORIES -------------------------------------------------------------

export interface CategoryOption {
  value: string
  label: Localized
  icon: LucideIcon
  /** True for categories that trigger an urgent, high-priority notice. */
  urgent?: boolean
}

export const CATEGORIES: CategoryOption[] = [
  {
    value: 'urgence',
    label: { fr: 'Urgence', en: 'Emergency' },
    icon: AlertTriangleIcon,
    urgent: true,
  },
  {
    value: 'eau',
    label: { fr: 'Eau potable', en: 'Drinking water' },
    icon: DropletIcon,
    urgent: true,
  },
  {
    value: 'travaux',
    label: { fr: 'Travaux', en: 'Public works' },
    icon: ConeIcon,
  },
  {
    value: 'collecte',
    label: { fr: 'Collecte', en: 'Waste collection' },
    icon: TrashIcon,
  },
  {
    value: 'loisirs',
    label: { fr: 'Loisirs', en: 'Recreation' },
    icon: TentTreeIcon,
  },
  {
    value: 'biblio',
    label: { fr: 'Bibliothèque', en: 'Library' },
    icon: BookOpenIcon,
  },
  {
    value: 'permis',
    label: { fr: 'Permis', en: 'Permits' },
    icon: FileCheckIcon,
  },
  {
    value: 'taxes',
    label: { fr: 'Taxes', en: 'Taxes' },
    icon: ReceiptIcon,
  },
  {
    value: 'evenement',
    label: { fr: 'Événement', en: 'Event' },
    icon: PartyPopperIcon,
  },
  {
    value: 'info',
    label: { fr: 'Information générale', en: 'General information' },
    icon: InfoIcon,
  },
]

export const getCategoryIcon = (value: string): LucideIcon =>
  CATEGORIES.find((c) => c.value === value)?.icon ?? InfoIcon

export const getCategoryLabel = (value: string): Localized =>
  CATEGORIES.find((c) => c.value === value)?.label ?? {
    fr: 'Général',
    en: 'General',
  }

// --- ADDRESS BOOK -----------------------------------------------------------

export interface AddressEntry {
  label: string
  coord: Coord
}

/** Mock Windsor address book powering the location autocomplete. */
export const WINDSOR_ADDRESSES: AddressEntry[] = [
  { label: '11 rue St-Georges, Windsor', coord: [45.5678, -71.9931] },
  { label: '250 rue Georges-Guilbault, Windsor', coord: [45.5701, -71.9884] },
  { label: '101 rue Ambroise-Dearden, Windsor', coord: [45.5643, -71.9968] },
  { label: '64 3e Avenue, Windsor', coord: [45.5719, -71.9962] },
  { label: '342 rue St-Georges, Windsor', coord: [45.5612, -71.9905] },
  { label: '1500 rue Principale Nord, Windsor', coord: [45.5752, -72.0018] },
  { label: '85 rue Bellevue, Windsor', coord: [45.5665, -72.0042] },
]

/** Estimated residents covered by a circular radius (metres). */
export const RADIUS_ESTIMATES: Record<string, number> = {
  '250': 3200,
  '500': 8600,
  '1000': 21400,
  '2000': 38900,
}

export const RADIUS_OPTIONS: { value: string; label: Localized }[] = [
  { value: '250', label: { fr: '250 mètres', en: '250 metres' } },
  { value: '500', label: { fr: '500 mètres', en: '500 metres' } },
  { value: '1000', label: { fr: '1 kilomètre', en: '1 kilometre' } },
  { value: '2000', label: { fr: '2 kilomètres', en: '2 kilometres' } },
]

// --- SECTORS ----------------------------------------------------------------

export interface Sector {
  id: string
  label: Localized
  population: number
  color: string
  polygon: Coord[]
}

export const SECTORS: Sector[] = [
  {
    id: 'abe',
    label: { fr: 'Secteurs A-B-E', en: 'Districts A-B-E' },
    population: 19320,
    color: '#4F46E5',
    polygon: [
      [45.5722, -71.9992],
      [45.5722, -71.9942],
      [45.5672, -71.9942],
      [45.5672, -71.9992],
    ],
  },
  {
    id: 'cijlmxy',
    label: { fr: 'Secteurs C-I-J-L-M-X-Y', en: 'Districts C-I-J-L-M-X-Y' },
    population: 32805,
    color: '#0EA5E9',
    polygon: [
      [45.5722, -71.9942],
      [45.5722, -71.9892],
      [45.5672, -71.9892],
      [45.5672, -71.9942],
    ],
  },
  {
    id: 'nos',
    label: { fr: 'Secteurs N-O-S', en: 'Districts N-O-S' },
    population: 15680,
    color: '#10B981',
    polygon: [
      [45.5672, -71.9992],
      [45.5672, -71.9942],
      [45.5622, -71.9942],
      [45.5622, -71.9992],
    ],
  },
  {
    id: 'prtv',
    label: { fr: 'Secteurs P-R-T-V', en: 'Districts P-R-T-V' },
    population: 23720,
    color: '#F59E0B',
    polygon: [
      [45.5672, -71.9942],
      [45.5672, -71.9892],
      [45.5622, -71.9892],
      [45.5622, -71.9942],
    ],
  },
]

/** Every sector of every city, for lookups by id. */
export const ALL_SECTORS: Sector[] = [...SECTORS, ...BROSSARD_SECTORS]

/** Sectors of the given city. */
export const sectorsFor = (city: City): Sector[] =>
  city === 'Brossard' ? BROSSARD_SECTORS : SECTORS

/** Map centre and zoom of the given city. */
export const mapViewFor = (city: City): { center: Coord; zoom: number } =>
  city === 'Brossard'
    ? { center: BROSSARD_CENTER, zoom: 12 }
    : { center: WINDSOR_CENTER, zoom: 13 }

/** Address book of the given city. */
export const addressesFor = (city: City): AddressEntry[] =>
  city === 'Brossard' ? BROSSARD_ADDRESSES : WINDSOR_ADDRESSES

export const TOTAL_CITIZENS = 91525

// --- PUBLIC SCREENS ---------------------------------------------------------

export interface ScreenLocation {
  id: string
  label: Localized
  /** Building the screen sits in. */
  place: Localized
  /** Average daily passers-by in front of the screen. */
  dailyViews: number
  online: boolean
}

/** Digital signage installed in municipal buildings. */
export const SCREEN_LOCATIONS: ScreenLocation[] = [
  {
    id: 'scr-hotel',
    label: { fr: 'Hall d’accueil', en: 'Main lobby' },
    place: { fr: 'Hôtel de ville', en: 'City Hall' },
    dailyViews: 640,
    online: true,
  },
  {
    id: 'scr-arena',
    label: { fr: 'Entrée et corridor', en: 'Entrance and corridor' },
    place: { fr: 'Centre sportif', en: 'Sports Centre' },
    dailyViews: 1850,
    online: true,
  },
  {
    id: 'scr-biblio',
    label: { fr: 'Comptoir de prêt', en: 'Circulation desk' },
    place: { fr: 'Bibliothèque municipale', en: 'Municipal Library' },
    dailyViews: 920,
    online: true,
  },
  {
    id: 'scr-piscine',
    label: { fr: 'Vestiaires', en: 'Change rooms' },
    place: { fr: 'Piscine municipale', en: 'Municipal Pool' },
    dailyViews: 480,
    online: true,
  },
  {
    id: 'scr-commu',
    label: { fr: 'Salle communautaire', en: 'Community hall' },
    place: { fr: 'Centre communautaire', en: 'Community Centre' },
    dailyViews: 310,
    online: false,
  },
]

export const SCREEN_DURATIONS: { value: string; label: Localized }[] = [
  { value: '8', label: { fr: '8 secondes', en: '8 seconds' } },
  { value: '12', label: { fr: '12 secondes', en: '12 seconds' } },
  { value: '20', label: { fr: '20 secondes', en: '20 seconds' } },
  { value: '30', label: { fr: '30 secondes', en: '30 seconds' } },
]

// --- FILTER AUDIENCE --------------------------------------------------------

export interface DepartmentOption {
  id: string
  label: Localized
  subscribers: number
}

/** "Détenteur de cartes" criterion — number of citizens holding each card. */
export const DEPARTMENTS: DepartmentOption[] = [
  {
    id: 'citoyenne',
    label: { fr: 'Carte citoyenne', en: 'Citizen card' },
    subscribers: 48200,
  },
  { id: 'loisirs', label: { fr: 'Loisir', en: 'Recreation' }, subscribers: 31200 },
  {
    id: 'biblio',
    label: { fr: 'Bibliothèque', en: 'Library' },
    subscribers: 24800,
  },
  { id: 'aucune', label: { fr: 'Aucune carte', en: 'No card' }, subscribers: 27300 },
]

export interface AgeBracket {
  id: string
  label: Localized
  share: number
}

export const AGE_BRACKETS: AgeBracket[] = [
  { id: '0-17', label: { fr: '0–17 ans', en: '0–17 years' }, share: 0.2 },
  { id: '18-34', label: { fr: '18–34 ans', en: '18–34 years' }, share: 0.22 },
  { id: '35-54', label: { fr: '35–54 ans', en: '35–54 years' }, share: 0.28 },
  { id: '55-64', label: { fr: '55–64 ans', en: '55–64 years' }, share: 0.14 },
  { id: '65+', label: { fr: '65 ans et +', en: '65 years and over' }, share: 0.16 },
]

/**
 * Residence status of the citizen relative to the municipality.
 * Residents live in Windsor; non-residents are registered users who do not
 * (property owners abroad, workers, users of municipal services).
 */
export interface ResidenceOption {
  value: string
  label: Localized
  share: number
}

export const RESIDENCE_OPTIONS: ResidenceOption[] = [
  { value: 'all', label: { fr: 'Tous', en: 'All' }, share: 1 },
  { value: 'resident', label: { fr: 'Résidents', en: 'Residents' }, share: 0.84 },
  {
    value: 'non-resident',
    label: { fr: 'Non-résidents', en: 'Non-residents' },
    share: 0.16,
  },
]

// --- MEDIA LIBRARY ----------------------------------------------------------

export interface MediaAsset {
  id: string
  url: string
  label: Localized
}

/** Shared municipal image bank offered when illustrating a notice. */
export const MEDIA_LIBRARY: MediaAsset[] = [
  {
    id: 'roadwork',
    url: 'https://cdn.magicpatterns.com/patterns/generated-images/f35afda7-f560-49b4-8ea4-f302bc4f825d.jpg',
    label: { fr: 'Travaux routiers', en: 'Road works' },
  },
  {
    id: 'waterworks',
    url: 'https://cdn.magicpatterns.com/patterns/generated-images/0693ccb0-4079-4ce1-a0c1-2c2f7f5f73c5.jpg',
    label: { fr: 'Eau potable', en: 'Drinking water' },
  },
  {
    id: 'community',
    url: 'https://cdn.magicpatterns.com/patterns/generated-images/c7825451-eb18-4dd9-a9d1-0bbd5bf80e28.jpg',
    label: { fr: 'Vie communautaire', en: 'Community life' },
  },
  {
    id: 'wastecollection',
    url: 'https://cdn.magicpatterns.com/patterns/generated-images/5bb92113-022f-4439-8b02-9c96a2edff76.jpg',
    label: { fr: 'Collecte des matières', en: 'Waste collection' },
  },
  {
    id: 'winterstorm',
    url: 'https://cdn.magicpatterns.com/patterns/generated-images/b81af284-f43f-4494-aec1-9c6189e9bb05.jpg',
    label: { fr: 'Tempête hivernale', en: 'Winter storm' },
  },
]

// --- TEMPLATES --------------------------------------------------------------

export interface NoticeTemplate {
  id: string
  /** Catalog metadata — part of the product UI, so it is translated. */
  name: Localized
  description: Localized
  /** Authored notice content — intentionally NOT translated. */
  title: string
  email: { subject: string; body: string }
  sms: string
  push: { title: string; message: string }
  social?: string
  /** Optional illustration pre-attached by the template. */
  imageUrl?: string
  /** True for templates saved by the team from a real notice. */
  custom?: boolean
}

const IMG = (id: string) => MEDIA_LIBRARY.find((m) => m.id === id)?.url

export const BUILT_IN_TEMPLATES: Record<string, NoticeTemplate[]> = {
  urgence: [
    {
      id: 'ebullition',
      name: { fr: "Avis d'ébullition d'eau", en: 'Boil water advisory' },
      description: {
        fr: "Préventif suite à un bris d'aqueduc",
        en: 'Preventive, following a water main break',
      },
      title: "Avis d'ébullition d'eau préventif",
      email: {
        subject: "Avis d'ébullition d'eau — Action requise",
        body: "Chers résidents,\n\nÀ la suite d'un bris d'aqueduc, nous vous demandons de faire bouillir l'eau du robinet pendant au moins une minute avant de la consommer. Cette mesure préventive demeure en vigueur jusqu'à nouvel ordre.\n\nMerci de votre collaboration.\nVille de Windsor",
      },
      sms: "AVIS D'ÉBULLITION : faites bouillir l'eau 1 min avant consommation jusqu'à nouvel ordre. Ville de Windsor.",
      push: {
        title: "Avis d'ébullition d'eau",
        message: "Faites bouillir l'eau 1 min avant de la consommer.",
      },
      social:
        "AVIS D'ÉBULLITION D'EAU — Faites bouillir l'eau du robinet 1 minute avant de la consommer, jusqu'à nouvel ordre.",
      imageUrl: IMG('waterworks'),
    },
    {
      id: 'meteo',
      name: { fr: 'Alerte météo majeure', en: 'Major weather alert' },
      description: {
        fr: 'Tempête ou conditions dangereuses',
        en: 'Storm or hazardous conditions',
      },
      title: 'Alerte météo majeure',
      email: {
        subject: 'Alerte météo majeure — Consignes de sécurité',
        body: 'Chers résidents,\n\nUne alerte météo majeure est en vigueur pour le secteur de Windsor. Nous vous invitons à limiter vos déplacements et à suivre les consignes des autorités.\n\nRestez en sécurité.\nVille de Windsor',
      },
      sms: 'ALERTE MÉTÉO : conditions dangereuses à Windsor. Limitez vos déplacements et suivez les consignes.',
      push: {
        title: 'Alerte météo majeure',
        message: 'Limitez vos déplacements. Suivez les consignes de sécurité.',
      },
      social:
        'ALERTE MÉTÉO — Conditions dangereuses à Windsor. Limitez vos déplacements et suivez les consignes des autorités.',
      imageUrl: IMG('winterstorm'),
    },
    {
      id: 'evacuation',
      name: { fr: 'Évacuation préventive', en: 'Preventive evacuation' },
      description: {
        fr: "Demande d'évacuation d'un secteur",
        en: 'Request to evacuate a district',
      },
      title: 'Évacuation préventive du secteur',
      email: {
        subject: 'Évacuation préventive — Instructions',
        body: "Chers résidents,\n\nPar mesure de précaution, une évacuation préventive est demandée pour votre secteur. Veuillez rassembler vos effets essentiels et vous diriger vers le centre d'hébergement désigné.\n\nVille de Windsor",
      },
      sms: 'ÉVACUATION PRÉVENTIVE de votre secteur demandée. Dirigez-vous vers le centre désigné.',
      push: {
        title: 'Évacuation préventive',
        message: 'Évacuation demandée pour votre secteur.',
      },
    },
  ],
  eau: [
    {
      id: 'ebullition-prev',
      name: { fr: "Avis d'ébullition préventif", en: 'Preventive boil advisory' },
      description: {
        fr: 'Travaux planifiés sur le réseau',
        en: 'Planned work on the network',
      },
      title: "Avis d'ébullition d'eau préventif",
      email: {
        subject: "Avis d'ébullition d'eau préventif",
        body: "Chers résidents,\n\nEn raison de travaux planifiés sur le réseau d'aqueduc, nous vous recommandons de faire bouillir l'eau avant consommation pour les prochaines 48 heures.\n\nVille de Windsor",
      },
      sms: "Avis préventif : faites bouillir l'eau du robinet pendant 48h en raison de travaux. Ville de Windsor.",
      push: {
        title: "Avis d'ébullition préventif",
        message: "Faites bouillir l'eau pendant 48h (travaux réseau).",
      },
      imageUrl: IMG('waterworks'),
    },
    {
      id: 'interruption-eau',
      name: { fr: "Interruption du service d'eau", en: 'Water service interruption' },
      description: { fr: 'Coupure temporaire', en: 'Temporary shutdown' },
      title: "Interruption temporaire du service d'eau",
      email: {
        subject: "Interruption temporaire du service d'eau",
        body: "Chers résidents,\n\nLe service d'eau sera interrompu temporairement dans votre secteur pour permettre des réparations. Le service devrait être rétabli dans la journée.\n\nVille de Windsor",
      },
      sms: "Interruption temporaire du service d'eau dans votre secteur. Rétablissement prévu dans la journée.",
      push: {
        title: "Interruption du service d'eau",
        message: 'Coupure temporaire dans votre secteur.',
      },
    },
  ],
  travaux: [
    {
      id: 'fermeture-rue',
      name: { fr: 'Fermeture de rue', en: 'Street closure' },
      description: { fr: 'Entrave à la circulation', en: 'Traffic obstruction' },
      title: 'Fermeture de rue temporaire',
      email: {
        subject: 'Fermeture de rue temporaire',
        body: 'Chers résidents,\n\nUne rue de votre secteur sera fermée temporairement à la circulation pour des travaux. Veuillez planifier un itinéraire alternatif.\n\nVille de Windsor',
      },
      sms: 'Fermeture de rue temporaire dans votre secteur pour travaux. Prévoyez un détour.',
      push: {
        title: 'Fermeture de rue',
        message: 'Une rue de votre secteur est fermée pour travaux.',
      },
      social:
        'FERMETURE DE RUE — Une rue de votre secteur est fermée temporairement pour travaux. Prévoyez un détour.',
      imageUrl: IMG('roadwork'),
    },
    {
      id: 'refection',
      name: { fr: 'Travaux de réfection routière', en: 'Road resurfacing work' },
      description: {
        fr: 'Réfection de chaussée planifiée',
        en: 'Planned pavement resurfacing',
      },
      title: 'Travaux de réfection routière',
      email: {
        subject: 'Travaux de réfection routière à venir',
        body: 'Chers résidents,\n\nDes travaux de réfection de la chaussée débuteront prochainement dans votre secteur. Des entraves à la circulation sont à prévoir.\n\nVille de Windsor',
      },
      sms: 'Travaux de réfection routière à venir dans votre secteur. Entraves à prévoir.',
      push: {
        title: 'Réfection routière',
        message: 'Travaux à venir dans votre secteur.',
      },
      imageUrl: IMG('roadwork'),
    },
  ],
  collecte: [
    {
      id: 'horaire-collecte',
      name: { fr: 'Modification de la collecte', en: 'Collection schedule change' },
      description: { fr: "Changement d'horaire", en: 'Schedule change' },
      title: "Modification de l'horaire de collecte",
      email: {
        subject: "Modification de l'horaire de collecte",
        body: "Chers résidents,\n\nVeuillez noter que l'horaire de collecte des matières résiduelles est modifié cette semaine en raison d'un jour férié.\n\nVille de Windsor",
      },
      sms: "L'horaire de collecte est modifié cette semaine (jour férié). Consultez le calendrier.",
      push: {
        title: 'Collecte modifiée',
        message: "L'horaire de collecte change cette semaine.",
      },
      imageUrl: IMG('wastecollection'),
    },
  ],
  evenement: [
    {
      id: 'evenement-municipal',
      name: { fr: 'Événement municipal', en: 'Municipal event' },
      description: { fr: 'Invitation à un événement', en: 'Invitation to an event' },
      title: 'Invitation — Événement municipal',
      email: {
        subject: 'Vous êtes invités à notre événement municipal',
        body: 'Chers résidents,\n\nNous avons le plaisir de vous inviter à un événement spécial organisé par la Ville de Windsor. Venez en grand nombre !\n\nVille de Windsor',
      },
      sms: 'Événement municipal à Windsor ce week-end. Venez en grand nombre !',
      push: {
        title: 'Événement municipal',
        message: 'Un événement spécial vous attend ce week-end.',
      },
      social:
        'Rendez-vous ce week-end pour notre événement municipal ! Activités pour toute la famille. #Windsor',
      imageUrl: IMG('community'),
    },
  ],
}

export const HASHTAG_SUGGESTIONS = [
  '#Windsor',
  '#AvisPublic',
  '#Travaux',
  '#Urgence',
  '#InfoWindsor',
]

/**
 * Template ids are prefixed with their category (`eau:boil`) so a single flat
 * list can hold every template, built-in or saved by the team.
 */
export function getTemplateCategory(template: NoticeTemplate): string {
  return template.id.split(':')[0]
}

/** Flattens the built-in catalogue into the editable, category-prefixed list. */
export function getInitialTemplates(): NoticeTemplate[] {
  return Object.entries(BUILT_IN_TEMPLATES).flatMap(([category, list]) =>
    list.map((tpl) => ({ ...tpl, id: `${category}:${tpl.id}` })),
  )
}

/** Channels a template actually fills, for display in lists. */
export function getTemplateChannels(template: NoticeTemplate): string[] {
  const used: string[] = []
  if (template.email.subject || template.email.body) used.push('email')
  if (template.sms) used.push('sms')
  if (template.push.title || template.push.message) used.push('push')
  if (template.social) used.push('social')
  return used
}
